/**
 * WebSocket封装
 * 功能：连接管理、心跳检测、自动重连、订阅/取消订阅、消息回调
 */

class WebSocketClient {
  constructor(options = {}) {
    // 配置项
    this.url = options.url || ''; // WebSocket地址
    this.heartbeatInterval = options.heartbeatInterval || 30000; // 心跳间隔（默认30秒）
    this.reconnectInterval = options.reconnectInterval || 5000; // 重连间隔（默认5秒）
    this.maxReconnectAttempts = options.maxReconnectAttempts || Infinity; // 最大重连次数
    this.token = options.token || ''; // 认证token
    
    // 状态
    this.ws = null; // WebSocket实例
    this.isConnected = false; // 是否已连接
    this.reconnectAttempts = 0; // 重连次数
    this.heartbeatTimer = null; // 心跳定时器
    this.reconnectTimer = null; // 重连定时器
    this.subscriptions = new Map(); // 订阅集合 key: topic, value: callback
    
    // 生命周期钩子
    this.onOpen = options.onOpen || (() => {});
    this.onClose = options.onClose || (() => {});
    this.onError = options.onError || (() => {});
    this.onMessage = options.onMessage || (() => {});
  }

  /**
   * 建立连接
   */
  connect() {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      console.log('[WebSocket] 已经连接，无需重复连接');
      return;
    }

    // 构建连接URL，添加token认证
    const connectUrl = this._buildConnectUrl();
    
    try {
      this.ws = new WebSocket(connectUrl);

      const self = this;
      this.ws.onopen = () => {
        console.log('[WebSocket] 连接成功, readyState:', self.ws.readyState);
        self.isConnected = true;
        self.reconnectAttempts = 0;
        
        console.log('[WebSocket] 开始发送认证...');
        self._sendAuth().then((success) => {
          console.log('[WebSocket] 认证完成, success:', success);
          self.onOpen();
          self._resubscribeAll();
        }).catch((err) => {
          console.error('[WebSocket] 认证异常:', err);
        });
      };

      this.ws.onclose = (event) => {
        console.log(`[WebSocket] 连接关闭，code: ${event.code}, reason: ${event.reason}`);
        self.isConnected = false;
        self._stopHeartbeat();
        self.onClose(event);
        // 自动重连
        self._tryReconnect();
      };

      this.ws.onerror = (error) => {
        console.error('[WebSocket] 连接错误', error);
        self.onError(error);
      };

      this.ws.onmessage = (event) => {
        self._handleMessage(event);
      };
    } catch (error) {
      console.error('[WebSocket] 创建连接失败', error);
      this._tryReconnect();
    }
  }

  /**
   * 关闭连接
   */
  disconnect() {
    this._stopHeartbeat();
    this._clearReconnect();
    
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    
    this.isConnected = false;
    console.log('[WebSocket] 主动断开连接');
  }

  /**
   * 订阅主题
   * @param {string} channel - 订阅主题
   * @param {Function} callback - 消息回调
   */
  subscribe(channel, callback) {
    if (this.isConnected) {
      this._sendSubscribe(channel);
    }
    this.subscriptions.set(channel, callback);
    console.log(`[WebSocket] 订阅主题: ${channel}`);
  }

  /**
   * 取消订阅
   * @param {string} channel - 取消订阅的主题
   */
  unsubscribe(channel) {
    if (this.isConnected) {
      this._sendUnsubscribe(channel);
    }
    this.subscriptions.delete(channel);
    console.log(`[WebSocket] 取消订阅: ${channel}`);
  }

  /**
   * 发送消息
   * @param {Object} data - 发送的数据
   */
  send(data) {
    if (!this.isConnected || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.error('[WebSocket] 连接未建立，无法发送消息, readyState:', this.ws ? this.ws.readyState : 'N/A');
      return false;
    }

    try {
      this.ws.send(JSON.stringify(data));
      console.log('[WebSocket] 消息发送成功:', data);
      return true;
    } catch (error) {
      console.error('[WebSocket] 发送消息失败', error);
      return false;
    }
  }

  /**
   * 更新token
   * @param {string} token - 新的token
   */
  updateToken(token) {
    this.token = token;
    // 如果已经连接，重连以使用新token
    if (this.isConnected) {
      this.disconnect();
      this.connect();
    }
  }

  // ------------------- 私有方法 -------------------

  /**
   * 构建连接URL，添加token参数
   */
  _buildConnectUrl() {
    const urlObj = new URL(this.url);
    if (this.token) {
      urlObj.searchParams.set('token', this.token);
    }
    return urlObj.toString();
  }

  /**
   * 处理接收的消息
   */
  _handleMessage(event) {
    try {
      const data = JSON.parse(event.data);

      // 心跳处理
      if (data.type === 'ping') {
        this._pong();
        return;
      }

      // 如果有对应主题的订阅，调用订阅回调
      if (data.channel && this.subscriptions.has(data.channel)) {
        const callback = this.subscriptions.get(data.channel);
        callback(data);
        return;
      }

      // 给全局回调
      this.onMessage(data);
    } catch (error) {
      console.error('[WebSocket] 解析消息失败', error);
      this.onMessage(event.data);
    }
  }

  /**
   * 开始心跳
   */
  _startHeartbeat() {
    this._stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      if (this.isConnected) {
        this._ping();
      }
    }, this.heartbeatInterval);
  }

  /**
   * 停止心跳
   */
  _stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  /**
   * 发送认证消息
   */
  _sendAuth() {
    return new Promise((resolve, reject) => {
      if (!this.token) {
        console.error('[WebSocket] 未找到token，无法发送认证消息');
        resolve(false);
        return;
      }

      const authMsg = {
        type: 'auth',
        token: this.token
      };
      console.log('[WebSocket] 发送认证消息:', authMsg);

      // 保存原始onMessage
      const originalOnMessage = this.onMessage;

      // 超时处理 - 10秒内没有收到认证响应则认为失败
      const timeoutId = setTimeout(() => {
        console.error('[WebSocket] 认证超时，10秒内未收到认证响应');
        this.onMessage = originalOnMessage; // 恢复原始onMessage
        resolve(false);
      }, 10000);

      // 监听认证响应
      this.onMessage = (data) => {
        console.log('[WebSocket] 收到消息:', data);
        if (data.type === 'auth') {
          clearTimeout(timeoutId); // 清除超时计时器
          if (data.success) {
            console.log('[WebSocket] 认证成功');
            this.onMessage = originalOnMessage;
            this._startHeartbeat(); // 认证成功后开始心跳
            resolve(true);
          } else {
            console.error('[WebSocket] 认证失败:', data.message);
            this.onMessage = originalOnMessage;
            resolve(false);
          }
        } else {
          originalOnMessage(data);
        }
      };

      if (!this.send(authMsg)) {
        console.error('[WebSocket] 发送认证消息失败');
        clearTimeout(timeoutId);
        this.onMessage = originalOnMessage;
        resolve(false);
      }
    });
  }

  /**
   * 发送心跳ping
   */
  _ping() {
    this.send({ type: 'ping', timestamp: Date.now() });
  }

  /**
   * 回复心跳pong
   */
  _pong() {
    this.send({ type: 'pong', timestamp: Date.now() });
  }

  /**
   * 尝试重连
   */
  _tryReconnect() {
    // 清除之前的重连定时器
    this._clearReconnect();
    
    // 检查是否超过最大重连次数
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error(`[WebSocket] 已达到最大重连次数 ${this.maxReconnectAttempts}，停止重连`);
      return;
    }

    this.reconnectAttempts++;
    console.log(`[WebSocket] ${this.reconnectInterval}ms后尝试第 ${this.reconnectAttempts} 次重连...`);
    
    this.reconnectTimer = setTimeout(() => {
      this.connect();
    }, this.reconnectInterval);
  }

  /**
   * 清除重连定时器
   */
  _clearReconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  /**
   * 重新订阅所有主题（重连成功后调用）
   */
  _resubscribeAll() {
    for (const channel of this.subscriptions.keys()) {
      this._sendSubscribe(channel);
    }
  }

  /**
   * 发送订阅消息
   */
  _sendSubscribe(channel) {
    this.send({
      type: 'subscribe',
      channel
    });
  }

  /**
   * 发送取消订阅消息
   */
  _sendUnsubscribe(channel) {
    this.send({
      type: 'unsubscribe',
      channel
    });
  }
}

// 单例实例
let instance = null;

/**
 * 获取WebSocket单例
 * @param {Object} options - 配置选项
 */
function getWebSocketClient(options = {}) {
  if (!instance) {
    instance = new WebSocketClient(options);
  } else if (options.url && options.url !== instance.url) {
    // 如果URL改变，更新并重连
    instance.url = options.url;
    if (options.token) {
      instance.token = options.token;
    }
    if (instance.isConnected) {
      instance.disconnect();
      instance.connect();
    }
  }
  return instance;
}

export {
  WebSocketClient,
  getWebSocketClient
};

export default WebSocketClient;
